import { Link, NavLink } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import SoundControls from "@/components/SoundControls";

const WORKLINE_ITEMS = [
    { to: "/theme/mountain-day", labelEn: "World Mountain Day", labelTr: "Dünya Dağlar Günü", monthEn: "December", monthTr: "Aralık", slug: "mountain-day" },
    { to: "/theme/green-to-blue", labelEn: "Green to Blue", labelTr: "Yeşilden Maviye", monthEn: "December", monthTr: "Aralık", slug: "green-to-blue" },
    { to: "/theme/january-mission", labelEn: "Mind Map", labelTr: "Zihin Haritası", monthEn: "January", monthTr: "Ocak", slug: "january-mission" },
    { to: "/theme/safer-internet", labelEn: "Safer Internet", labelTr: "Güvenli İnternet", monthEn: "February", monthTr: "Şubat", slug: "safer-internet" },
    { to: "/theme/april-earth", labelEn: "Earth Day & Blue Research", labelTr: "Dünya Günü & Mavi Ekonomi", monthEn: "April", monthTr: "Nisan", slug: "april-earth" },
    { to: "/theme/may-etwinning", labelEn: "eTwinning Day ★", labelTr: "eTwinning Günü ★", monthEn: "May", monthTr: "Mayıs", slug: "may-etwinning", featured: true },
];

export default function Header() {
    const { lang, t } = useT();
    const [open, setOpen] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);
    const dropRef = useRef(null);
    const closeMenu = () => setOpen(false);
    const closeDrop = () => setDropOpen(false);

    const TOP_NAV = [
        { to: "/theme/new-year-wishes", label: t("nav.newYear"), testid: NAV.link("new-year-wishes") },
        { to: "/theme/renewable-energy", label: t("nav.renewable"), testid: NAV.link("renewable-energy") },
        { to: "/theme/introductions", label: t("nav.students"), testid: NAV.link("introductions") },
        { to: "/schools", label: t("nav.schools"), testid: NAV.schoolsLink },
        { to: "/about", label: t("nav.about"), testid: NAV.aboutLink },
    ];

    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) {
                setDropOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <header
            data-testid={NAV.root}
            className="sticky top-0 z-50 backdrop-blur-xl bg-bg-base/85 border-b border-black/5"
        >
            <div className="container-pad flex items-center justify-between h-16 md:h-20 gap-4">
                <Link to="/" data-testid={NAV.brand} className="flex items-center gap-3 group shrink-0">
                    <img
                        src="/images/stemcycle-g2b-logo.jpg"
                        alt="STEMcycle G2B logo"
                        className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover ring-1 ring-black/5"
                    />
                    <div className="leading-tight hidden sm:block">
                        <div className="heading-serif text-xl text-ink-primary">
                            STEMcycle <span className="gradient-g2b-text">G2B</span>
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                            Green to Blue · Innovation Lab
                        </div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-6 flex-1 justify-end">
                    <div className="relative" ref={dropRef}>
                        <button
                            type="button"
                            data-testid="nav-worklines-toggle"
                            onClick={() => setDropOpen((o) => !o)}
                            className="inline-flex items-center gap-1.5 text-[15px] font-medium text-ink-secondary hover:text-brand-blue transition-colors"
                        >
                            {t("nav.worklines")}
                            <ChevronDown
                                size={14}
                                strokeWidth={1.6}
                                className={`transition-transform duration-200 ${dropOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {dropOpen && (
                            <div
                                data-testid="nav-worklines-menu"
                                className="absolute right-0 top-full mt-3 w-80 rounded-2xl bg-white border border-black/5 shadow-[0_20px_60px_rgba(15,46,69,0.12)] p-3"
                            >
                                <div className="caption text-brand-green px-3 pb-2">
                                    {t("nav.monthlyChapters")}
                                </div>
                                {WORKLINE_ITEMS.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        data-testid={NAV.link(item.slug)}
                                        onClick={closeDrop}
                                        className={({ isActive }) =>
                                            `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                                isActive
                                                    ? "bg-brand-green/10 text-brand-green"
                                                    : item.featured
                                                    ? "text-brand-blue-dark bg-brand-gold/10 hover:bg-brand-gold/20"
                                                    : "text-ink-primary hover:bg-bg-alt"
                                            }`
                                        }
                                    >
                                        <span className="text-sm font-medium">{lang === "tr" ? item.labelTr : item.labelEn}</span>
                                        <span className={`text-[10px] uppercase tracking-[0.18em] ${item.featured ? "text-brand-gold font-semibold" : "text-ink-muted"}`}>
                                            {lang === "tr" ? item.monthTr : item.monthEn}
                                        </span>
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {TOP_NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `text-[15px] font-medium transition-colors duration-200 ${
                                    isActive ? "text-brand-green" : "text-ink-secondary hover:text-brand-blue"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3 shrink-0">
                    <LanguageToggle variant="light" />
                    <SoundControls variant="light" />
                </div>

                <button
                    type="button"
                    aria-label="Toggle menu"
                    data-testid={NAV.mobileToggle}
                    onClick={() => setOpen((o) => !o)}
                    className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10 text-ink-primary"
                >
                    {open ? <X size={18} strokeWidth={1.6} /> : <Menu size={18} strokeWidth={1.6} />}
                </button>
            </div>

            {open && (
                <div className="xl:hidden border-t border-black/5 bg-white max-h-[80vh] overflow-y-auto">
                    <div className="container-pad py-4">
                        <div className="flex items-center gap-3 mb-4 md:hidden">
                            <LanguageToggle variant="light" />
                            <SoundControls variant="light" />
                        </div>
                        <div className="caption text-brand-green mb-2">{t("nav.worklines")}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mb-4">
                            {WORKLINE_ITEMS.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`${NAV.link(item.slug)}-mobile`}
                                    onClick={closeMenu}
                                    className={({ isActive }) =>
                                        `flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium ${
                                            isActive
                                                ? "bg-brand-green/10 text-brand-green"
                                                : item.featured
                                                ? "bg-brand-gold/10 text-brand-blue-dark hover:bg-brand-gold/20"
                                                : "text-ink-secondary hover:bg-bg-alt"
                                        }`
                                    }
                                >
                                    <span>{lang === "tr" ? item.labelTr : item.labelEn}</span>
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                                        {lang === "tr" ? item.monthTr : item.monthEn}
                                    </span>
                                </NavLink>
                            ))}
                        </div>
                        <div className="caption text-brand-blue mb-2">{t("nav.more")}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {TOP_NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`${item.testid}-mobile`}
                                    onClick={closeMenu}
                                    className={({ isActive }) =>
                                        `block px-3 py-3 rounded-lg text-sm font-medium ${
                                            isActive ? "bg-brand-green/10 text-brand-green" : "text-ink-secondary hover:bg-bg-alt"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
